import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Bot, LayoutGrid, Users, ClipboardList, Rocket, CalendarDays, Stethoscope, Library,
  Plus, LogOut, ChevronDown, Loader2,
} from "lucide-react";
import AgentsGrid from "@/components/agency/AgentsGrid";
import BriefingForm from "@/components/agency/BriefingForm";
import PipelineRunner from "@/components/agency/PipelineRunner";
import CalendarView from "@/components/agency/CalendarView";
import DiagnosisView from "@/components/agency/DiagnosisView";
import LibraryView from "@/components/agency/LibraryView";

const TABS = [
  { key: "agentes", label: "Agentes", icon: Users },
  { key: "briefing", label: "Briefing", icon: ClipboardList },
  { key: "pipeline", label: "Criar Conteúdo", icon: Rocket },
  { key: "calendario", label: "Calendário", icon: CalendarDays },
  { key: "diagnostico", label: "Diagnóstico", icon: Stethoscope },
  { key: "biblioteca", label: "Biblioteca", icon: Library },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [brands, setBrands] = useState([]);
  const [activeBrand, setActiveBrand] = useState(null);
  const [tab, setTab] = useState("agentes");
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);

  const loadBrands = async (selectId) => {
    try {
      const res = await api.get("/brands");
      setBrands(res.data);
      if (res.data.length) {
        const pick = res.data.find((b) => b.brand_id === selectId) || res.data.find((b) => b.brand_id === activeBrand?.brand_id) || res.data[0];
        setActiveBrand(pick);
      } else {
        setActiveBrand(null);
      }
    } catch (e) { toast.error("Erro ao carregar marcas."); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadBrands(); /* eslint-disable-next-line */ }, []);

  if (loading) {
    return <div className="agency-bg min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>;
  }

  return (
    <div className="agency-bg min-h-screen text-slate-100">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-800/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
              <Bot className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-display font-extrabold tracking-tight hidden sm:block">AGÊNCIA<span className="text-emerald-400">.AI</span></span>
          </div>

          {/* Brand selector */}
          <div className="flex items-center gap-2">
            <Select value={activeBrand?.brand_id || ""} onValueChange={(id) => setActiveBrand(brands.find((b) => b.brand_id === id))}>
              <SelectTrigger data-testid="brand-selector" className="w-[200px] bg-slate-900/60 border-slate-700 h-9">
                <SelectValue placeholder="Selecione a marca" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {brands.map((b) => <SelectItem key={b.brand_id} value={b.brand_id}>{b.nome}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={newOpen} onOpenChange={setNewOpen}>
              <DialogTrigger asChild>
                <Button data-testid="btn-new-brand" size="icon" variant="outline" className="h-9 w-9 border-slate-700 hover:border-emerald-500/50">
                  <Plus className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-950 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display">Nova marca</DialogTitle></DialogHeader>
                <BriefingForm onSaved={(b) => { setNewOpen(false); loadBrands(b.brand_id); setTab("pipeline"); }} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex-1" />

          <Button data-testid="btn-header-criar" onClick={() => setTab("pipeline")}
            className="hidden md:inline-flex bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold hover:brightness-110 transition-all h-9">
            <Rocket className="w-4 h-4 mr-2" /> Criar Conteúdo
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2">
                <Avatar className="w-8 h-8 border border-slate-700">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-slate-800 text-emerald-400 text-xs">{(user?.name || "U")[0]}</AvatarFallback>
                </Avatar>
                <ChevronDown className="w-4 h-4 text-slate-500 hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-900 border-slate-700 text-slate-100">
              <div className="px-2 py-1.5 text-xs text-slate-400">{user?.email}</div>
              <DropdownMenuItem data-testid="btn-logout" onClick={logout} className="cursor-pointer focus:bg-slate-800">
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Nav tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 overflow-x-auto">
          <div className="flex gap-1 pb-2">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} data-testid={`nav-${t.key}`} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all ${active ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "text-slate-400 hover:text-slate-200 border border-transparent"}`}>
                  <Icon className="w-4 h-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!activeBrand && tab !== "agentes" && tab !== "briefing" ? (
          <div className="glass rounded-2xl p-10 text-center text-slate-400">
            <LayoutGrid className="w-10 h-10 mx-auto mb-3 text-slate-600" />
            Crie ou selecione uma marca para continuar.
            <div className="mt-4"><Button onClick={() => setNewOpen(true)} className="bg-emerald-500 text-slate-950">Nova marca</Button></div>
          </div>
        ) : (
          <>
            {tab === "agentes" && <AgentsGrid />}
            {tab === "briefing" && (
              <BriefingForm key={activeBrand?.brand_id || "new"} brand={activeBrand}
                onSaved={(b) => loadBrands(b.brand_id)} />
            )}
            {tab === "pipeline" && <PipelineRunner brand={activeBrand} onDone={() => {}} />}
            {tab === "calendario" && <CalendarView brand={activeBrand} />}
            {tab === "diagnostico" && <DiagnosisView brand={activeBrand} />}
            {tab === "biblioteca" && <LibraryView brand={activeBrand} />}
          </>
        )}
      </main>
    </div>
  );
}
